import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Camera, Eye, IdCard, Pencil, Plus, Trash2 } from "lucide-react";
import { api, apiBlob, apiUpload } from "@/lib/api-client";
import { Button } from "@/components/ui/Button";
import { DateInput } from "@/components/ui/DateInput";
import type { Booking, BookingGuest } from "@/types/domain";

// Guest register (police register) of a booking: every adult's identity details, children's name and age,
// optional ID photos. The server refuses check-in until every adult is complete (when the property requires it).

const ID_TYPES = ["Aadhaar", "Driving licence", "Voter ID", "PAN", "Passport", "Other"];
const isForeigner = (nationality?: string | null) => Boolean(nationality && !/^indian?$/i.test(nationality.trim()));

// Same rule as the server (guestRegisterService.missingGuestFields).
export const missingGuestFields = (guest: BookingGuest) => {
  if (guest.isChild) return guest.age != null || guest.dateOfBirth ? [] : ["age"];
  const missing: string[] = [];
  if (!guest.gender) missing.push("gender");
  if (guest.age == null && !guest.dateOfBirth) missing.push("age");
  if (!guest.address) missing.push("address");
  if (!guest.nationality) missing.push("nationality");
  if (!guest.idType || !guest.idNumber) missing.push("ID");
  if (isForeigner(guest.nationality) && !guest.passportNumber) missing.push("passport");
  return missing;
};

export const registerStatus = (booking: Booking) => {
  const adults = booking.adults ?? booking.noOfGuests;
  const complete = (booking.guests ?? []).filter((guest) => !guest.isChild && missingGuestFields(guest).length === 0).length;
  return { adults, complete, missing: Math.max(0, adults - complete) };
};

export function GuestRegister({ booking, canEdit, canSeeIds, required, openForm, onFormOpened, onUpdated }: {
  booking: Booking;
  canEdit: boolean;
  canSeeIds: boolean;
  required: boolean;
  /** Set from outside (check-in blocked) to open the form for the next missing adult. */
  openForm?: boolean;
  onFormOpened?: () => void;
  onUpdated: (booking: Booking) => void;
}) {
  const [editing, setEditing] = useState<BookingGuest | "new" | null>(null);
  const [removing, setRemoving] = useState<BookingGuest | null>(null);
  const [photo, setPhoto] = useState<{ guestId: string; side: "front" | "back"; url: string } | null>(null);
  const status = registerStatus(booking);
  const guests = booking.guests ?? [];

  useEffect(() => {
    if (!openForm) return;
    const incomplete = guests.find((guest) => !guest.isChild && missingGuestFields(guest).length);
    setEditing(incomplete ?? "new");
    onFormOpened?.();
  }, [openForm]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = useMutation({
    mutationFn: (guestId: string) => api<Booking>(`/bookings/${booking.id}/guests/${guestId}`, { method: "DELETE" }),
    onSuccess: (updated) => { setRemoving(null); onUpdated(updated); },
  });
  const upload = useMutation({
    mutationFn: ({ guestId, side, file }: { guestId: string; side: "front" | "back"; file: File }) => {
      const form = new FormData();
      form.append("photo", file);
      return apiUpload<Booking>(`/bookings/${booking.id}/guests/${guestId}/id-photo?side=${side}`, form);
    },
    onSuccess: onUpdated,
  });
  const view = useMutation({
    mutationFn: async ({ guestId, side }: { guestId: string; side: "front" | "back" }) => ({ guestId, side, url: URL.createObjectURL(await apiBlob(`/bookings/${booking.id}/guests/${guestId}/id-photo/${side}`)) }),
    onSuccess: (result) => setPhoto((current) => { if (current) URL.revokeObjectURL(current.url); return result; }),
  });

  return (
    <div className="guest-register">
      <div className={status.missing ? "guest-register__status is-missing" : "guest-register__status"}>
        <IdCard size={16} aria-hidden="true" />
        <span>Guest register: {status.complete} of {status.adults} adult(s) complete{status.missing && required ? " - needed before check-in" : ""}</span>
        {canEdit && !editing && <button type="button" className="booking-line-section__add" onClick={() => setEditing("new")}><Plus size={13} /> Add guest</button>}
      </div>
      {guests.map((guest) => {
        const missing = missingGuestFields(guest);
        return (
          <div key={guest.id} className="guest-register__row">
            <div className="guest-register__main">
              <strong>{guest.name}{guest.isPrimary ? " · main guest" : ""}{guest.isChild ? " · child" : ""}</strong>
              <small>
                {[guest.gender?.toLowerCase(), guest.age != null ? `${guest.age} yrs` : guest.dateOfBirth?.slice(0, 10), guest.nationality, guest.idType && guest.idNumber ? `${guest.idType} ••${guest.idNumber.replace(/\s/g, "").slice(-4)}` : null].filter(Boolean).join(" · ")}
              </small>
              {missing.length > 0 && <small className="guest-register__missing">Missing: {missing.join(", ")}</small>}
            </div>
            <span className="guest-register__actions">
              {(["front", "back"] as const).map((side) => {
                const has = side === "front" ? guest.idFrontPath : guest.idBackPath;
                return (
                  <span key={side} className="guest-register__photo">
                    {canEdit && (
                      <label className="button button--secondary guest-register__upload" title={`${has ? "Replace" : "Add"} ID photo (${side})`}>
                        <Camera size={14} /> {side}
                        <input type="file" accept="image/*" capture="environment" hidden onChange={(event) => { const file = event.target.files?.[0]; if (file) upload.mutate({ guestId: guest.id, side, file }); event.target.value = ""; }} />
                      </label>
                    )}
                    {has && canSeeIds && <button type="button" className="icon-button" aria-label={`View ID ${side}`} onClick={() => view.mutate({ guestId: guest.id, side })}><Eye size={15} /></button>}
                    {has && !canSeeIds && <small>{side} ✓</small>}
                  </span>
                );
              })}
              {canEdit && <button type="button" className="icon-button" aria-label={`Edit ${guest.name}`} onClick={() => setEditing(guest)}><Pencil size={15} /></button>}
              {canEdit && <button type="button" className="icon-button" aria-label={`Remove ${guest.name}`} onClick={() => setRemoving(guest)}><Trash2 size={15} /></button>}
            </span>
            {removing?.id === guest.id && (
              <div className="bd-confirm" role="alertdialog">
                <p><strong>Remove {guest.name} from the guest register?</strong> Their details and ID photos are deleted.</p>
                {remove.error && <div className="form-error">{remove.error.message}</div>}
                <div className="bd-actions__row">
                  <Button variant="secondary" disabled={remove.isPending} onClick={() => setRemoving(null)}>Go back</Button>
                  <Button variant="danger" loading={remove.isPending} onClick={() => remove.mutate(guest.id)}>Yes, remove</Button>
                </div>
              </div>
            )}
            {photo?.guestId === guest.id && (
              <figure className="guest-register__preview">
                <img src={photo.url} alt={`ID ${photo.side} of ${guest.name}`} />
                <Button variant="ghost" onClick={() => { URL.revokeObjectURL(photo.url); setPhoto(null); }}>Close</Button>
              </figure>
            )}
          </div>
        );
      })}
      {!guests.length && <p className="booking-line-section__empty">No guest details yet.</p>}
      {(upload.error || view.error) && <div className="form-error">{(upload.error ?? view.error)?.message}</div>}
      {editing && (
        <GuestForm
          bookingId={booking.id}
          guest={editing === "new" ? null : editing}
          defaults={editing === "new" && !guests.length ? { name: booking.guestName, phone: booking.guestPhone } : undefined}
          onCancel={() => setEditing(null)}
          onSaved={(updated) => { setEditing(null); onUpdated(updated); }}
        />
      )}
    </div>
  );
}

type FormState = Record<string, string | boolean>;
const toForm = (guest: BookingGuest | null, defaults?: { name: string; phone: string }): FormState => ({
  name: guest?.name ?? defaults?.name ?? "",
  isChild: guest?.isChild ?? false,
  phone: guest?.phone ?? defaults?.phone ?? "",
  relationName: guest?.relationName ?? "",
  dateOfBirth: guest?.dateOfBirth?.slice(0, 10) ?? "",
  age: guest?.age != null ? String(guest.age) : "",
  gender: guest?.gender ?? "",
  address: guest?.address ?? "",
  city: guest?.city ?? "",
  state: guest?.state ?? "",
  pincode: guest?.pincode ?? "",
  country: guest?.country ?? "India",
  nationality: guest?.nationality ?? "Indian",
  idType: guest?.idType ?? "Aadhaar",
  idNumber: guest?.idNumber ?? "",
  comingFrom: guest?.comingFrom ?? "",
  goingTo: guest?.goingTo ?? "",
  purpose: guest?.purpose ?? "",
  passportNumber: guest?.passportNumber ?? "",
  passportExpiry: guest?.passportExpiry?.slice(0, 10) ?? "",
  visaNumber: guest?.visaNumber ?? "",
  visaType: guest?.visaType ?? "",
  visaExpiry: guest?.visaExpiry?.slice(0, 10) ?? "",
  arrivedInIndiaOn: guest?.arrivedInIndiaOn?.slice(0, 10) ?? "",
});

function GuestForm({ bookingId, guest, defaults, onCancel, onSaved }: { bookingId: string; guest: BookingGuest | null; defaults?: { name: string; phone: string }; onCancel: () => void; onSaved: (booking: Booking) => void }) {
  const [form, setForm] = useState<FormState>(() => toForm(guest, defaults));
  const set = (key: string) => (event: { target: { value: string } }) => setForm((current) => ({ ...current, [key]: event.target.value }));
  const text = (key: string) => String(form[key] ?? "");
  const child = Boolean(form.isChild);
  const foreign = isForeigner(text("nationality"));
  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = { ...form, age: text("age") === "" ? null : Number(text("age")) };
      for (const key of Object.keys(body)) if (body[key] === "") body[key] = null;
      body.name = text("name").trim();
      body.isChild = child;
      if (!body.gender) body.gender = null;
      return guest
        ? api<Booking>(`/bookings/${bookingId}/guests/${guest.id}`, { method: "PATCH", body })
        : api<Booking>(`/bookings/${bookingId}/guests`, { method: "POST", body });
    },
    onSuccess: onSaved,
  });
  const valid = text("name").trim().length >= 2;

  return (
    <section className="booking-inline-form guest-form">
      <h4>{guest ? `Edit ${guest.name}` : "Add guest"}</h4>
      <label className="checkbox-row"><input type="checkbox" checked={child} onChange={(event) => setForm((current) => ({ ...current, isChild: event.target.checked }))} /> Child (only name and age needed)</label>
      <div className="property-form-grid">
        <label>Full name<input value={text("name")} onChange={set("name")} /></label>
        <label>Age<input type="number" min={0} max={120} value={text("age")} onChange={set("age")} /></label>
        {!child && (
          <>
            <label>Father / husband name<input value={text("relationName")} onChange={set("relationName")} /></label>
            <label>Gender
              <select value={text("gender")} onChange={set("gender")}>
                <option value="">Select</option><option value="MALE">Male</option><option value="FEMALE">Female</option><option value="OTHER">Other</option>
              </select>
            </label>
            <label>Date of birth (or age above)<DateInput value={text("dateOfBirth")} onChange={set("dateOfBirth")} /></label>
            <label>Mobile<input inputMode="tel" value={text("phone")} onChange={set("phone")} /></label>
            <label className="property-form-grid__wide">Address<input value={text("address")} onChange={set("address")} /></label>
            <label>City<input value={text("city")} onChange={set("city")} /></label>
            <label>State<input value={text("state")} onChange={set("state")} /></label>
            <label>Pincode<input inputMode="numeric" value={text("pincode")} onChange={set("pincode")} /></label>
            <label>Country<input value={text("country")} onChange={set("country")} /></label>
            <label>Nationality<input value={text("nationality")} onChange={set("nationality")} /></label>
            <label>ID type
              <select value={text("idType")} onChange={set("idType")}>{ID_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}</select>
            </label>
            <label>ID number<input value={text("idNumber")} onChange={set("idNumber")} /></label>
            <label>Coming from<input value={text("comingFrom")} onChange={set("comingFrom")} /></label>
            <label>Going to<input value={text("goingTo")} onChange={set("goingTo")} /></label>
            <label>Purpose of visit<input value={text("purpose")} placeholder="e.g. Darshan, business" onChange={set("purpose")} /></label>
            {foreign && (
              <>
                <label>Passport number<input value={text("passportNumber")} onChange={set("passportNumber")} /></label>
                <label>Passport expiry<DateInput value={text("passportExpiry")} onChange={set("passportExpiry")} /></label>
                <label>Visa number<input value={text("visaNumber")} onChange={set("visaNumber")} /></label>
                <label>Visa type<input value={text("visaType")} onChange={set("visaType")} /></label>
                <label>Visa expiry<DateInput value={text("visaExpiry")} onChange={set("visaExpiry")} /></label>
                <label>Arrived in India on<DateInput value={text("arrivedInIndiaOn")} onChange={set("arrivedInIndiaOn")} /></label>
              </>
            )}
          </>
        )}
      </div>
      {save.error && <div className="form-error">{save.error.message}</div>}
      <div className="booking-inline-form__actions">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="button" loading={save.isPending} disabled={!valid} onClick={() => save.mutate()}>Save guest</Button>
      </div>
    </section>
  );
}
